import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, PHONE_SHORT, PHONE_TEL } from "../../data/site";

export const Navbar = () => {
    const [hidden, setHidden] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let last = 0;
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 60);
            setHidden(y > 160 && y > last);
            last = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const linkClass = ({ isActive }) =>
        `text-[11px] font-medium tracking-[0.28em] transition-colors duration-300 ${
            isActive ? "text-bronze" : "text-bone/60 hover:text-bone"
        }`;

    return (
        <>
            <header
                data-testid="main-nav"
                className={`fixed inset-x-0 top-0 z-50 transition-[transform,background-color,border-color] duration-500 ${
                    hidden && !open ? "-translate-y-full" : "translate-y-0"
                } ${
                    scrolled
                        ? "border-b border-white/[0.06] bg-[#0a0a0b]/85 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="flex h-20 items-center justify-between px-5 md:h-24 md:px-10">
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        className="group flex items-center"
                    >
                        <img
                            src="/logo.png"
                            alt="Ramljak Auto Centar — logo"
                            className="h-14 w-auto transition-opacity duration-300 group-hover:opacity-80 md:h-[76px]"
                        />
                    </Link>

                    <nav className="hidden items-center gap-10 md:flex">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.id}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-link-${l.id}`}
                                className={linkClass}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/termin"
                            data-testid="nav-cta-book"
                            className="bg-bone px-5 py-2.5 text-[11px] font-semibold tracking-[0.24em] text-[#0a0a0b] transition-colors duration-300 hover:bg-bronze"
                        >
                            ZAKAŽI TERMIN
                        </Link>
                    </nav>

                    <button
                        data-testid="nav-menu-button"
                        onClick={() => setOpen(true)}
                        aria-label="Otvori izbornik"
                        className="flex h-10 w-10 items-center justify-center text-bone md:hidden"
                    >
                        <Menu size={22} strokeWidth={1.5} />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-[60] flex flex-col bg-[#0a0a0b]/98 backdrop-blur-2xl"
                    >
                        <div className="flex h-16 items-center justify-between px-5">
                            <img
                                src="/logo.png"
                                alt="Ramljak Auto Centar — logo"
                                className="h-14 w-auto"
                            />
                            <button
                                data-testid="mobile-menu-close"
                                onClick={() => setOpen(false)}
                                aria-label="Zatvori izbornik"
                                className="flex h-10 w-10 items-center justify-center text-bone"
                            >
                                <X size={22} strokeWidth={1.5} />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-1 px-8">
                            {[
                                ...NAV_LINKS,
                                { label: "ZAKAŽI TERMIN", to: "/termin", id: "termin" },
                            ].map((l, i) => (
                                <motion.div
                                    key={l.id}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.08 + i * 0.07, duration: 0.55 }}
                                >
                                    <NavLink
                                        to={l.to}
                                        end={l.to === "/"}
                                        data-testid={`mobile-nav-link-${l.id}`}
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) =>
                                            `block border-b border-white/[0.06] py-5 font-display text-4xl font-light tracking-wide transition-colors hover:text-bronze ${
                                                l.id === "termin"
                                                    ? "italic text-bronze"
                                                    : isActive
                                                      ? "text-bronze"
                                                      : "text-bone"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <motion.a
                            href={PHONE_TEL}
                            data-testid="mobile-menu-call"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="px-8 pb-12 text-sm tracking-[0.3em] text-silver"
                        >
                            {PHONE_SHORT}
                        </motion.a>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
